import { Player } from "@lottiefiles/react-lottie-player";
import SubdirectoryArrowRightIcon from "@mui/icons-material/SubdirectoryArrowRight";
import { Button,Dialog,DialogActions,DialogContent,DialogTitle,Drawer,Stack,TextField,Typography } from "@mui/material";
import CircularProgress from "@mui/material/CircularProgress";
import { useMutation,useQuery } from "@tanstack/react-query";
import { useContext,useEffect,useState } from "react";
import { useTranslation } from "react-i18next";
import { useLocation,useParams } from "react-router-dom";
import { toast } from "react-toastify";
import axios from "axios";
import { UserContext } from "../../../context/UserContext";
import { WebFormContext } from "../../../context/WebFormContext";
import AttToken from "../../../helpers/attToken";
import { trackEventMatomo,trackEventMatomoVisit } from "../../../helpers/matomo";
import Checklists from "../../../service/Checklists";
import queryClient from "../../../service/query";
import { FormToFilling,ItemFormFilling,OcorrenciaType,SubItemFormFilling } from "../../../types/new-form-filling/types";
import { modeloStyleType } from "../../../types/print-template/types";
import { executaForm } from "../../helpers/checklists";
import { Paragraph } from "../../Notifications/style/SingleNotificationStyle";
import CheckFilling from "../CheckFilling";
import DrawerList from "./DrawerList";
import MultipleSubitem from "./MultipleSubitem";
import NewFormFillingFooter from "./NewFormFillingFooter";
import NewFormFillingHeader from "./NewFormFillingHeader";
import SubItemFilling from "./SubItemFilling";

const checkListService = new Checklists();
const TIMEOUTDURATION = 4000;
export const estadoCompartilhado = {
  inicio: Date.now(),
};

type responseType = {
  formToFilling: FormToFilling;
  styleOptions?: modeloStyleType;
};

export type ExecutionContext = {
  inicio: number;
  checklist?: FormToFilling;
  isResuming: boolean;
  resumeError?: string;
  pausaExpiraEm?: number | null;
  execExpiraEm?: number | null;
};

export type PublicFormExecution = {
  checklist: FormToFilling;
  concluir: (respostas: FormToFilling) => Promise<{ status: 'autorizado' | 'pendente' }>;
};

function mensagemErroConclusaoPublica(error: unknown, mensagemPadrao: string) {
  if (!axios.isAxiosError(error)) return mensagemPadrao;
  const dados = error.response?.data as { error?: string; message?: string } | undefined;
  return dados?.error || dados?.message || mensagemPadrao;
}

export default function NewFormFilling({
  id,
  close,
  executionContext,
  onExecutionFinished,
  onPause,
  onCancel,
  isPausing,
  publicExecution,
}: {
  id?: string,
  close?: Function,
  executionContext?: ExecutionContext,
  onExecutionFinished?: () => void,
  onPause?: () => void,
  onCancel?: () => void,
  isPausing?: boolean,
  publicExecution?: PublicFormExecution,
}) {
  const { setWebForm, webForm, fotos, arquivos, setFotos, setArquivos, ForceUpdate } = useContext(WebFormContext);
  const [onStartScreen, setOnStartScreen] = useState(false);
  const { search } = useLocation();
  const [completed, setCompleted] = useState(false);
  const [publicResult, setPublicResult] = useState<'autorizado' | 'pendente' | null>(null);
  const [confirmFinishOpen, setConfirmFinishOpen] = useState(false);
  const [itemToShow, setItemToShow] = useState<number>(0);
  const [open, setOpen] = useState(false);
  const { logado, setLogado, selectedCliente, selectedLocal, selectedConjunto, selectedUnidade, userData } = useContext(UserContext);
  const { t } = useTranslation();

  const locate = useLocation();

  useEffect(() => {
    if (locate.pathname.includes("/preenchedor")) {
      trackEventMatomoVisit('Preenchedor de formulário externo');
      (() => {
        const body = document.getElementById("root");
        if (body) {
          body.style.backgroundColor = "#E5E5E5";
          body.style.margin = "0";
          body.style.padding = "0";
          body.style.height = "100%";
        }
      })();
    }
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, []);

  const { formId } = useParams();
  const inicioExecucao = executionContext?.inicio ?? estadoCompartilhado.inicio;
  
  const { data, isLoading, isFetching, isSuccess, isError, refetch } = useQuery({
    queryKey: ["GET_FORM_DATA", executionContext?.inicio, publicExecution?.checklist.id],
    queryFn: async () => {
      if (publicExecution) {
        setLogado(false);
        return { formToFilling: publicExecution.checklist };
      }
      if (executionContext?.checklist) {
        setLogado(true);
        return { formToFilling: executionContext.checklist };
      }
      if (id) {
        toast.info("Buscando modelo para preenchimento", { toastId: "buscando" });
        const token = await AttToken();
        if (token) {
          const response = await checkListService.getChecklistForWebFilling(token, id, false);
          setLogado(true);
          return response;
        }
      }
      if (formId) {
        const response: responseType = await checkListService.getChecklistForWebFilling(null, formId, true);
        setLogado(false);
        return response;
      }
    },
    retry: 3,
    refetchOnWindowFocus: false,
    refetchOnMount: "always",
  });

  useEffect(() => {
    if (!locate.pathname.includes('/preenchedor')) return;
    if (isSuccess) trackEventMatomo('preenchedor de formulário externo', 'sucesso', 'carregamento', 'formulário carregado');
    if (isError) trackEventMatomo('preenchedor de formulário externo', 'erro', 'carregamento', 'falha ao carregar formulário');
  }, [isError, isSuccess, locate.pathname]);

  const { mutate, isPending } = useMutation({
    mutationKey: ["EXECUTE_FORM"],
    mutationFn: async () => {
      const promise = toast.promise(
        async () => {
          if (publicExecution) {
            return publicExecution.concluir(webForm);
          }
          if (logado) {
            const files = [...fotos, ...arquivos];
            const response = await executaForm(
              inicioExecucao,
              webForm,
              true,
              files,
              selectedCliente,
              selectedConjunto,
              selectedLocal,
              selectedUnidade,
              userData ?? "",
              search ?? "",
              false,
              executionContext?.isResuming ? true : undefined,
            );
            return response;
          }
          if (formId) {
            const response = await executaForm(
              inicioExecucao,
              webForm,
              false,
              [],
              selectedCliente,
              selectedConjunto,
              selectedLocal,
              selectedUnidade,
              userData ?? "",
              search,
            );
            return response;
          }
        },
        {
          pending: t("formFilling.executing"),
          success: t("formFilling.executed"),
          error: publicExecution
            ? { render: ({ data: error }) => mensagemErroConclusaoPublica(error, t("formFilling.error")) }
            : t("formFilling.error"),
        },
        { toastId: "executando" },
      );
      return promise;
    },
    onSuccess: (result?: { status?: 'autorizado' | 'pendente' }) => {
      void queryClient.invalidateQueries({ queryKey: ['GET_PAUSED_EXECUTIONS'] });
      onExecutionFinished?.();
      setCompleted(true);
      setPublicResult(result?.status ?? null);
      setFotos([]);
      setArquivos([]);
      if (close) {
        setTimeout(() => {
          close(true);
        }, TIMEOUTDURATION);
      }
    },
  });

  useEffect(() => {
    if (data) {
      if (data?.styleOptions && data?.styleOptions?.enablePostMessage) {
        setOnStartScreen(true);
      }
      if (!data?.styleOptions?.associatedChecklists?.includes(data?.formToFilling?.id as string)) {
        delete data?.styleOptions;
      }
      if (data?.formToFilling) {
        const formItens = Array.isArray(data.formToFilling.itens)
          ? data.formToFilling.itens
          : [];
        const initializedItens = formItens.map((item: ItemFormFilling) => {
          const patchedSubitens = item?.subitens?.map((subitem: SubItemFormFilling) => ({
            ...subitem,
            itemid: subitem.itemid ?? item.id,
          }));

          if (!item.ocorrenciaMultipla || executionContext?.isResuming) return { ...item, subitens: patchedSubitens };

          const firstOcorrencia: OcorrenciaType = {
            nome: item.nomeOcorrencia ? `${item.nomeOcorrencia} 1` : "Ocorrência 1",
            id: 0,
            subitens: patchedSubitens?.map((subitem: SubItemFormFilling) => ({ ...subitem })) ?? [],
            itemId: item.id,
          };
          return { ...item, subitens: patchedSubitens, ocorrencias: [firstOcorrencia, ...(item.ocorrencias ?? [])] };
        });
        setWebForm({
          ...(executionContext?.checklist ?? { ...data.formToFilling, itens: initializedItens ?? [] }),
          inicio: inicioExecucao,
        });
      }
    }
    // A submissão não deve reinicializar a árvore de respostas: em caso de erro
    // na API de conclusão, os valores preenchidos precisam permanecer na tela.
  }, [data, executionContext?.checklist, executionContext?.isResuming, inicioExecucao, logado, setWebForm]);

  useEffect(() => {
    if (completed && data?.styleOptions?.enableLoop) {
      setTimeout(() => {
        if (data.styleOptions.enablePostMessage) {
          setOnStartScreen(true);
        }
        setCompleted(false);
      }, TIMEOUTDURATION);
    }
    if (completed && data.styleOptions?.enableRedirect && data.styleOptions.link) {
      setTimeout(() => {
        window.open(data.styleOptions.link);
      }, TIMEOUTDURATION);
    }
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [completed, isLoading]);

  useEffect(() => {
    ForceUpdate();
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [webForm]);

  if (completed && data?.styleOptions?.enablePosMessage) {
    return (
      <div className='postMessageDiv' style={{ padding: '5% 10% 5% 10%' }}>
        {data.styleOptions.imageAfter && (
          <img style={{ maxWidth: '20%' }} src={data.styleOptions.imageAfter} alt="aaa" />
        )}
        <TextField
          type="text"
          inputProps={{
            min: 0,
            style: { textAlign: "center" },
          }}
          disabled
          fullWidth
          multiline
          value={data.styleOptions.messageAfter}
        />
      </div>
    );
  }

  async function FillAgain() {
    estadoCompartilhado.inicio = Date.now();
    setOnStartScreen(true);
    await refetch();
    setItemToShow(0);
    setCompleted(false);
  }

  if (completed && !data?.styleOptions?.enablePosMessage) {
    return (
      <Stack
        justifyContent='center'
        alignItems='center'
        minHeight={publicExecution ? '100svh' : '100%'}
        width='100%'
      >
        <Player
          src="https://lottie.host/79da71c7-a35b-4581-be5a-cd7aa5e4f644/UGaGhP6cDm.json"
          className="player"
          style={{ height: '50%', width: '50%' }}
          autoplay
          keepLastFrame
        />
        {publicResult && <Typography variant="h5" textAlign="center">
          {publicResult === 'autorizado'
            ? t('publicCheckin.success')
            : t('publicCheckin.pending')}
        </Typography>}
        {!logado && !publicExecution &&
          <Button style={{ color: '#390053', borderColor: '#390053', flex: 'none', flexShrink: 0 }}
            variant="outlined" size="large" onClick={() => { FillAgain(); }}>
            {t('formFilling.fillAgain')}
          </Button>}
      </Stack>
    );
  }

  const scrollToElement = (id: string) => {
    setTimeout(() => {
      const elemento: HTMLCollectionOf<HTMLElement> = document?.getElementsByClassName(id) as HTMLCollectionOf<HTMLElement>;
      if (elemento && elemento[0]) {
        elemento[0].style.border = "1px solid red";
        elemento[0]?.scrollIntoView({ behavior: "smooth", block: "center" });
      } else if (elemento && elemento[0]) {
        elemento[0].style.border = "1px solid gray";
      }
    }, 999);
  };

  function onClick() {
    const res = CheckFilling(webForm);
    toast.dismiss();
    for (const result of res) {
      if (!result.success) {
        if (result?.form) {
          toast.error(result.texto, {
            position: "top-right",
            autoClose: false,
            hideProgressBar: true,
            closeOnClick: false,
            pauseOnHover: true,
            draggable: true,
            progress: undefined,
            theme: "colored",
          });
        } else {
          scrollToElement(`${result.itemId}|${result.subItemId}`);
          toast.error(
            `${t("formFilling.subitem")}
          ${result.subItem?.toUpperCase()}\n, 
          ${t("formFilling.onItem")} ${result.item?.toUpperCase()}\n 
          ${result.texto}`,
            {
              toastId: `${result.itemId}|${result.subItemId}`,
              position: "top-right",
              autoClose: false,
              hideProgressBar: true,
              closeOnClick: false,
              pauseOnHover: true,
              draggable: true,
              progress: undefined,
              theme: "colored",
            },
          );
          setItemToShow(Number(result.itemId?.slice(-1)));
        }
      }
      if (result.success) {
        mutate();
      }
    }
  }

  if (onStartScreen && !isLoading && data?.styleOptions) {
    return (
      <Stack direction="column" justifyContent="center" alignItems="center" spacing={2} height="100%" padding={"5% 10% 5% 10%"}>
        {data.styleOptions.imageBefore && <img style={{ maxWidth: "20%" }} src={data?.styleOptions?.imageBefore} alt="aaa" />}
        <TextField
          onClick={() => trackEventMatomo("preenchimento", "click", "textarea", "edita valor")}
          type="text"
          inputProps={{
            min: 0,
            style: { textAlign: "center" },
          }}
          disabled
          fullWidth
          multiline
          value={data?.styleOptions?.messageBefore}
        />
        <Button endIcon={<SubdirectoryArrowRightIcon />} type="button" className="button is-success" onClick={() => setOnStartScreen(false)}>
          Prosseguir
        </Button>
      </Stack>
    );
  }

  function Loading() {
    return (
      <Stack sx={{ height: `${formId ? "100svh" : ""}` }} direction="column" justifyContent="center" alignItems="center">
        <svg width={0} height={0}>
          <defs>
            <linearGradient id="my_gradient" x1="0%" y1="0%" x2="0%" y2="100%">
              <stop offset="0%" stopColor="#e01cd5" />
              <stop offset="100%" stopColor="#1CB5E0" />
            </linearGradient>
          </defs>
        </svg>
        <CircularProgress sx={{ "svg circle": { stroke: "url(#my_gradient)" } }} />
        <Typography variant="h4">{t("formFilling.loadingForm")}</Typography>
      </Stack>
    );
  }

  if (isLoading || isFetching) {
    return <Loading />;
  }

  if (!webForm || !Array.isArray(webForm.itens) || webForm.itens.length === 0) {
    return (
      <Stack sx={{ height: "100svh" }} direction="column" justifyContent="center" alignItems="center">
        <Typography variant="h4">{t("formFilling.notFound")}</Typography>
      </Stack>
    );
  }

  const currentItem = webForm?.itens[itemToShow];
  const hasMandatoryUnfilled = currentItem?.ocorrenciaMultipla
    ? currentItem.ocorrencias?.some(oc => oc.subitens.some((ele) => ele.obrigatoriedade?.preenchimento && !ele.checked))
    : currentItem?.subitens?.some((ele) => ele.obrigatoriedade?.preenchimento && !ele.checked);

  return (
    <Stack
      sx={{
        backgroundColor: "#E5E5E5",
        minHeight: publicExecution ? '100svh' : undefined,
        pointerEvents: publicExecution && isPending ? 'none' : 'auto',
      }}
      width={"100%"}
      direction="column"
      aria-busy={isPending}
    >
      <NewFormFillingHeader form={webForm} setOpen={setOpen} styleOptions={data?.styleOptions} />
      <Drawer sx={{ zIndex: 999 }} open={open} onClose={() => setOpen(false)}>
        {<DrawerList itens={webForm?.itens} setOpen={setOpen} setItemToShow={setItemToShow} />}
      </Drawer>
      {itemToShow !== null && (
        <Stack
          sx={{
            width: "100%",
            p: `${window.navigator.maxTouchPoints <= 0 ? "5% 10% 15% 10%" : "5% 2% 25% 2%"}`,
            height: "100%",
          }}
          direction="column"
          justifyContent="center"
          alignItems="center"
          spacing={2}>
          <Stack width={"100%"}>
            {hasMandatoryUnfilled && (
              <Paragraph> (*) {t("formFilling.mandatoryFill")} </Paragraph>
            )}
            <Typography variant="h5">Item - {webForm && webForm?.itens[itemToShow]?.nome}</Typography>
          </Stack>
          {!webForm?.itens[itemToShow]?.ocorrenciaMultipla && webForm?.itens[itemToShow].subitens?.map((ele: SubItemFormFilling) => (
            <Stack width={"100%"} key={`Item:${ele.itemid}|Subitem:${ele.id}`} direction="column" justifyContent="space-between">
              <SubItemFilling subItem={ele} />
            </Stack>
          ))}
          {webForm && webForm?.itens[itemToShow]?.ocorrenciaMultipla && (
            <MultipleSubitem webForm={webForm} itemToShow={itemToShow} item={webForm.itens[itemToShow]} setWebForm={setWebForm} />
          )}
        </Stack>
      )}
      {
        <NewFormFillingFooter
          logado={logado}
          itemToShow={itemToShow}
          setItemToShow={setItemToShow}
          isLoading={isPending}
          executa={() => executionContext ? setConfirmFinishOpen(true) : onClick()}
          checklist={webForm}
          styleOptions={data?.styleOptions}
          onlyOneItem={webForm && webForm.itens?.length <= 1}
          onPause={onPause}
          onCancel={onCancel}
          isPausing={isPausing}
        />
      }
      <Dialog open={confirmFinishOpen} onClose={() => !isPending && setConfirmFinishOpen(false)}>
        <DialogTitle>Finalizar execução?</DialogTitle>
        <DialogContent>
          <Typography>Deseja mesmo finalizar a execução do formulário?</Typography>
        </DialogContent>
        <DialogActions>
          <Button onClick={() => setConfirmFinishOpen(false)} disabled={isPending}>Cancelar</Button>
          <Button
            color="success"
            variant="contained"
            onClick={() => {
              setConfirmFinishOpen(false);
              onClick();
            }}
            disabled={isPending}
          >
            Finalizar
          </Button>
        </DialogActions>
      </Dialog>
    </Stack>
  );
}